import {
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useScreenClass } from "react-grid-system";
import styled from "styled-components";
import Button from "../../components/Button";
import FlexBox, { FlexGrow } from "../../components/FlexBox";
import { Col, Row } from "../../components/StyledGridSystem";
import axios from "axios";
import { UserContext } from "../../context/UserContext";
import { UniversityContext } from "../../context/UniversityContext";
import { useNavigate } from "react-router-dom";
import StyledCalendar from "../../components/Calendar";
import FilterModal from "../../components/FilterModal";
import Status from "../../types/enum/Status";
import Icon from "../../components/Icon";
import { StyledText } from "../../components/Text";
import { Icon as MuiIcon } from "@mui/material";
import moment from "moment";
import StyledActivity from "../../components/StyledActivity";
import generateActivity, {
  generateCalendarDates,
  generateTime,
} from "../../hooks/generateActivity";
import LoadingSpinner from "../../components/LoadingSpinner";
import { convertToSelect } from "../../hooks/useScript";
import Pill from "../../components/Pill";
import { FilterBar } from "./FilterBar";
import { CompetencyFilterModal } from "./CompetencyFilterModal";
import { PersonFilterModal } from "./PersonFilterModal";
import SnackBar from "../../components/SnackBar";
import { StyledAddButton } from "../RoadMap/RoadMap";
import { handleJWTError } from "../../hooks/jwtErrHandler";
import { getProgramShortNameForSemesterApi } from "../../utils/programName";
const CustomRow = styled(Row)`
  margin-bottom: 0px;
  height: calc(100vh - 135px);
  height: -moz-calc(100vh - 135px);
  height: -o-calc(100vh - 135px);
  height: -webkit-calc(100vh - 135px);
`;
const CustomFlex = styled(FlexBox)`
  position: absolute;
  top: -40px;
  width: 78%;
  z-index: 1002;
`;
const CustomPill = styled(FlexBox)`
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 1002;
`;
const StyledBlock = styled.div`
  background: #b52b37;
  padding: 5px 12px;
  cursor: pointer;
  border: 1px solid #e7ebee;
  box-sizing: border-box;
  backdrop-filter: blur(33px);
  border-radius: 6px;
  white-space: nowrap;
`;

function ActivityCalendarPage() {
  const screenClass = useScreenClass();
  const navigate = useNavigate();
  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [semesterDataLoaded, setSemesterDataLoaded] = useState<boolean>(false);
  const calendarRequestIdRef = useRef(0);
  const [eventTypesLoaded, setEventTypesLoaded] = useState<boolean>(false);
  const [dates, setDates] = useState<Array<any>>([]);
  const [times, setTimes] = useState<Array<any>>([]);
  const [events, setEvents] = useState<Array<any>>([]);
  const [initialLength, setInitialLength] = useState<number>(0);
  const [filterData, setFilterData] = useState<Array<any>>([]);
  const [filterCode, setFilterCode] = useState<string>("");
  const [tempFilterCode, setTempFilterCode] = useState<string>("");
  const [filterPerson, setFilterPerson] = useState<string>("");
  const [tempFilterPerson, setTempFilterPerson] = useState<string>("");

  const [competencyCodes, setCompetencyCodes] = useState<Array<string>>([]);
  const [filters, setFilters] = useState<Array<any>>([]);
  const [filterCompetencyChecked, setFilterCompetencyChecked] =
    useState<boolean>(true);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [codeFilterOpen, setCodeFilterOpen] = useState<boolean>(false);
  const [personFilterOpen, setPersonFilterOpen] = useState<boolean>(false);

  const [startDate, setStartDate] = useState<any>(
    moment().startOf("isoWeek").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<any>(
    moment().endOf("isoWeek").format("YYYY-MM-DD")
  );
  const [isTypeFiltered, setIsTypeFiltered] = useState(false);
  const [isCodeFiltered, setIsCodeFiltered] = useState(false);
  const [isPersonFiltered, setIsPersonFiltered] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [programOptions, setProgramOptions] = useState<Array<any>>([]);
  const [selectedProgram, setSelectedProgram] = useState<any>(null);

  const currentUser = useContext(UserContext).user;
  const universityContext = useContext(UniversityContext);

  useEffect(() => {
    if (filterData.length === 0) {
      fetchEventTypes();
    } else {
      setEventTypesLoaded(true);
    }

    // Only fetch semesters after event types are loaded
    if (eventTypesLoaded && currentUser.role === 1) {
      fetchSemesters(startDate, endDate);
    }
  }, [startDate, endDate, eventTypesLoaded, filters, isCodeFiltered,
    isPersonFiltered, universityContext?.selectedProgram, filterCompetencyChecked]);

  useEffect(() => {
    const fetchData = async () => {
      setDates(generateCalendarDates(startDate, endDate));
      setTimes(generateTime());

      // Students with "My Competencies" still load via fetchSemesters.
      if (currentUser.role === 1 && !semesterDataLoaded && filterCompetencyChecked) {
        return;
      }

      // Instructors: wait until event types and identity are ready so the
      // first "My Events" pass is not run against empty filters / empty name.
      if (currentUser.role !== 1) {
        if (!eventTypesLoaded || !currentUser.name) {
          return;
        }
        await fetchCalendar(startDate, endDate, null);
      }
    };

    fetchData();
  }, [
    filters,
    filterCompetencyChecked,
    isCodeFiltered,
    isPersonFiltered,
    startDate,
    endDate,
    competencyCodes,
    semesterDataLoaded,
    eventTypesLoaded,
    universityContext?.selectedProgram,
    currentUser,
  ]);


  useEffect(() => {
    universityContext.setProgram({
      label: "ALL Programs",
      value: "ALL",
      degree_abbrev: "",
      program_abbrev: "",
      id: "",
      description: "",
      first_semester: "",
      program_name: "All Programs",
      short_name: "",
      university_code: "",
      versions: [],
    });
  }, []);

  const [calendarSemester, setCalendarSemester] = useState("")
  const fetchSemesters = async (startDate: any, endDate: any) => {
    setLoading(true);
    setSemesterDataLoaded(false);

    axios
      .get(
        `/calendar/semester?start_date=${startDate}&end_date=${endDate}&university_code=${currentUser.university_code || "CMKL"
        }`
      )
      .then((res) => {
        setCalendarSemester(res.data.semester)
        if (res.data.semester && res.data.semester !== "" && res.data.semester!==calendarSemester) {
          axios
            .get(
              `/student/cards/semester?student_id=${currentUser.id}&semester_name=${res.data.semester}&program_name=${currentUser.programs?.[0]?.program_name}`
            )
            .then((res: any) => {
              if (
                res.data.card_info.cards &&
                res.data.card_info.cards.length > 0
              ) {
                let compCodes: Array<string> = [];
                res.data.card_info.cards.forEach(
                  (card: { competency_code: string }) => {
                    compCodes.push(card.competency_code);
                  }
                );
                setCompetencyCodes(compCodes);
                fetchCalendar(startDate, endDate, compCodes);
              } else {
                setCompetencyCodes([]);
                fetchCalendar(startDate, endDate, null);
              }
            })
            .catch((err) => {
              handleJWTError(err);
              setCompetencyCodes([]);
              fetchCalendar(startDate, endDate, null);

            });
        } else {
          // setCompetencyCodes([]);
          setLoading(false)
          fetchCalendar(startDate, endDate, null);
        }
      })
      .catch((err) => {
        handleJWTError(err);
        setCompetencyCodes([]);
      });
  };
  const fetchCalendar = async (startDate: any, endDate: any, stuCompetencies: any) => {
    const requestId = ++calendarRequestIdRef.current;
    setLoading(true);

    const programShortName = getProgramShortNameForSemesterApi(
      universityContext.selectedProgram
    );
    const programParam = programShortName
      ? `&program_name=${encodeURIComponent(programShortName)}`
      : "";

    axios
      .get(
        `/calendar/events?start_date=${startDate}&end_date=${endDate}${programParam}&university_code=CMKL`
      )
      .then((res) => {
        if (requestId !== calendarRequestIdRef.current) {
          return;
        }
        if (res.data.events) {
          (initialLength === 0 || initialLength !== res.data.events.length) &&
            setInitialLength(res.data.events.length);
          const filteredByEventType = res.data.events.filter(
            (item: { event_type_val: any }) =>
              filters.includes(item.event_type_val)
          );

          const filterByCode = res.data.events.filter(
            (v: any) =>
              filterCode ===
              (v?.related_competency?.competency_code || v?.competency_code)
          );

          let searchArray = filterPerson.trim().split(" ");
          let re = new RegExp(searchArray.join("|"), "i");
          // let temp = this.events.filter((event: any) =>
          //   re.test(event.responsible_person)
          // );

          const filterByPerson = res.data.events.filter(
            (v: any) => re.test(v.responsible_person)
            // (v: any) => filterPerson === v?.responsible_person
          );
          const intersect =
            isTypeFiltered && isCodeFiltered && isPersonFiltered
              ? filteredByEventType
                .filter((value: any) => filterByCode.includes(value))
                .filter((value: any) => filterByPerson.includes(value))
              : isTypeFiltered && isCodeFiltered && !isPersonFiltered
                ? filteredByEventType.filter((value: any) =>
                  filterByCode.includes(value)
                )
                : isTypeFiltered && !isCodeFiltered && isPersonFiltered
                  ? filteredByEventType.filter((value: any) =>
                    filterByPerson.includes(value)
                  )
                  : isTypeFiltered && !isCodeFiltered && !isPersonFiltered
                    ? filteredByEventType
                    : !isTypeFiltered && isCodeFiltered && isPersonFiltered
                      ? filterByCode.filter((value: any) =>
                        filterByPerson.includes(value)
                      )
                      : !isTypeFiltered && isCodeFiltered && !isPersonFiltered
                        ? filterByCode
                        : !isTypeFiltered && !isCodeFiltered && isPersonFiltered
                          ? filterByPerson
                          : filteredByEventType;
          if (filterCompetencyChecked) {
            const filteredByComp = stuCompetencies ? intersect.filter((item: any) =>
              stuCompetencies.includes(
                item.related_competency
                  ? item.related_competency.competency_code
                  : ""
              )
            ) : intersect.filter((item: any) =>
              competencyCodes.includes(
                item.related_competency
                  ? item.related_competency.competency_code
                  : ""
              )
            );
            const filteredByInstructor = intersect.filter((item: any) => {
              const responsible = item?.responsible_person;
              const instructorName = currentUser.name;
              if (!instructorName || responsible == null) {
                return false;
              }
              if (typeof responsible === "string") {
                return responsible.includes(instructorName);
              }
              if (Array.isArray(responsible)) {
                return responsible.some((person: any) =>
                  String(person).includes(instructorName)
                );
              }
              return String(responsible).includes(instructorName);
            });
            if (currentUser.role === 1) {
              const events = generateActivity(filteredByComp, startDate);
              setEvents(events);
            } else {
              const events = generateActivity(filteredByInstructor, startDate);
              setEvents(events);
            }
          } else if (!filterCompetencyChecked) {
            const events = generateActivity(intersect, startDate);
            setEvents(events);
          } else {
            setEvents([]);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        if (requestId !== calendarRequestIdRef.current) {
          return;
        }
        handleJWTError(err);
        setEvents([]);
        setLoading(false);
      });
  };
  const fetchEventTypes = async () => {
    axios
      .get("/calendar/event_types")
      .then((res) => {
        if (res.data.event_types) {
          setFilterData(
            convertToSelect(res.data.event_types, "title", "value")
          );
          const custArray: any = [];
          res.data.event_types.forEach((type: { value: any }) => {
            custArray.push(type.value);
          });
          setFilters(custArray);
        }
        setEventTypesLoaded(true);
        setLoading(false);
      })
      .catch((err) => {
        handleJWTError(err);
        setEventTypesLoaded(true);
        setLoading(false);
      });
  };
  const preWeek = (start: any, end: any) => {
    setStartDate(
      moment(
        new Date(new Date(start).setDate(new Date(start).getDate() - 7))
      ).format("YYYY-MM-DD")
    );
    setEndDate(
      moment(
        new Date(new Date(end).setDate(new Date(end).getDate() - 7))
      ).format("YYYY-MM-DD")
    );
  };
  const nextWeek = (start: any, end: any) => {
    setStartDate(
      moment(
        new Date(new Date(start).setDate(new Date(start).getDate() + 7))
      ).format("YYYY-MM-DD")
    );
    setEndDate(
      moment(
        new Date(new Date(end).setDate(new Date(end).getDate() + 7))
      ).format("YYYY-MM-DD")
    );
  };

  const resetAllFilter = () => {
    setIsTypeFiltered(false);
    setIsCodeFiltered(false);
    setIsPersonFiltered(false);
    setFilterCompetencyChecked(false);
    const index: any = [];
    filterData.forEach((element) => {
      index.push(element.value);
    });
    setIsTypeFiltered(false);
    setFilterCode("");
    setFilterPerson("");
    fetchEventTypes();
  };
  if (!universityContext.programs) return <LoadingSpinner />;
  return (
    <>
      <FilterModal
        isTypeFiltered={isTypeFiltered}
        setIsTypeFiltered={setIsTypeFiltered}
        modalHeader=""
        filterData={filterData}
        setOpen={setFilterOpen}
        inset={isMobile ? "-82px auto auto 0px" : "-82px auto auto 217px"}
        mobile={isMobile}
        isOpen={filterOpen}
        myCompetencyChecked={filterCompetencyChecked}
        filters={filters}
        setFilters={setFilters}
        confirmFunction={(eventTypes: any, myCompChecked: boolean) => {
          if (eventTypes.length === 0) {
            setIsWarningOpen(true);
          } else {
            setFilters(eventTypes);
            setFilterOpen(false);
            setIsTypeFiltered(true);
          }
          // setIsTypeFiltered(true);
          // setFilterCompetencyChecked(myCompChecked);
        }}
      />
      <CompetencyFilterModal
        filterCode={filterCode}
        setFilterCode={setFilterCode}
        isCodeFiltered={isCodeFiltered}
        setIsCodeFiltered={setIsCodeFiltered}
        modalHeader=""
        filterData={filterData}
        setOpen={setCodeFilterOpen}
        inset={isMobile ? "-82px auto auto 0px" : "-82px auto auto 217px"}
        mobile={isMobile}
        isOpen={codeFilterOpen}
        filters={filters}
        tempFilterCode={tempFilterCode}
        setTempFilterCode={setTempFilterCode}
        confirmFunction={async () => {
          await fetchCalendar(startDate, endDate, null);
          setIsCodeFiltered(true);
          // setFilterCompetencyChecked(myCompChecked);
        }}
      />
      <PersonFilterModal
        filterPerson={filterPerson}
        setFilterPerson={setFilterPerson}
        isPersonFiltered={isPersonFiltered}
        setIsPersonFiltered={setIsPersonFiltered}
        modalHeader=""
        filterData={filterData}
        setOpen={setPersonFilterOpen}
        inset={isMobile ? "-82px auto auto 0px" : "-82px auto auto 217px"}
        mobile={isMobile}
        isOpen={personFilterOpen}
        filters={filters}
        tempFilterPerson={tempFilterPerson}
        setTempFilterPerson={setTempFilterPerson}
        confirmFunction={async () => {
          setIsPersonFiltered(true);
          if (tempFilterPerson !== filterPerson) {
            await fetchCalendar(startDate, endDate, null);
          }
          // setFilterCompetencyChecked(myCompChecked);
        }}
      />
      <SnackBar
        isOpen={isWarningOpen}
        setOpen={setIsWarningOpen}
        message={"Please select one of the event types"}
        header="Warning!"
        type="warning"
      />
      <CustomFlex>
        {!isMobile && (
          <FlexGrow style={{ flexGrow: 1, maxWidth: "fit-content" }}>
            <StyledBlock>
              <MuiIcon
                sx={{
                  fontSize: "1.1em",
                  verticalAlign: "text-top",
                  marginRight: "5px",
                }}
              >
                calendar_today
              </MuiIcon>
              Weekly Calendar
            </StyledBlock>
          </FlexGrow>
        )}
        <FlexGrow style={{ flexBasis: "fit-content", marginLeft: "10px" }}>
          <Button
            onClick={() => {
              preWeek(startDate, endDate);
            }}
            isOutline
            status={Status.border}
            style={{ padding: "5px 12px" }}
          >
            {"<"}
          </Button>
          {!isMobile && window.screen.width > 1079 && (
            <StyledText
              style={{
                margin: "0 16px",
              }}
            >
              {startDate}
            </StyledText>
          )}
          <StyledText style={{ padding: "0 5px" }}>-</StyledText>
          {!isMobile && window.screen.width > 1079 && (
            <StyledText
              style={{
                margin: "0 16px",
              }}
            >
              {endDate}
            </StyledText>
          )}
          <Button
            onClick={() => {
              nextWeek(startDate, endDate);
            }}
            isOutline
            status={Status.border}
            style={{ padding: "5px 12px" }}
          >
            {">"}
          </Button>
        </FlexGrow>
      </CustomFlex>
      <FlexBox alignItems="center" style={{ marginBottom: 10, gap: 10 }}>
        <FilterBar
          programOptions={programOptions}
          selectedProgram={selectedProgram}
          tempFilterCode={tempFilterCode}
          tempFilterPerson={tempFilterPerson}
          isCodeFiltered={isCodeFiltered}
          isTypeFiltered={isTypeFiltered}
          isPersonFiltered={isPersonFiltered}
          setFilterCompetencyChecked={setFilterCompetencyChecked}
          filterCompetencyChecked={filterCompetencyChecked}
          setFilterOpen={setFilterOpen}
          filterOpen={filterOpen}
          setPersonFilterOpen={setPersonFilterOpen}
          personFilterOpen={personFilterOpen}
          setCodeFilterOpen={setCodeFilterOpen}
          codeFilterOpen={codeFilterOpen}
          setSelectedProgram={setSelectedProgram}
        />
        <StyledAddButton
          disabled={false}
          style={{
            height: "47px",
            // padding: "3px 15px",
          }}
          status={Status.button}
          onClick={() => {
            resetAllFilter();
          }}
        >
          <Icon color={"#FFFFFF"} name="filter-slash" mr={1} /> Show All Events
        </StyledAddButton>
      </FlexBox>
      <CustomRow
        wrap="nowrap"
        style={{ marginLeft: "-12px", marginRight: "-10px" }}
      >
        <Col style={{ padding: 0 }}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <StyledCalendar dateRange={dates} timeRange={times}>
              {events.length > 0 ? (
                <>
                  {events.map((event: any) => (
                    <StyledActivity
                      info={event}
                      title={event.title}
                      startTime={event.startTime}
                      endTime={event.endTime}
                      left={event.left}
                      top={event.top}
                      height={event.height}
                      width={event.width}
                    />
                  ))}
                </>
              ) : (
                <CustomPill>
                  <Pill
                    style={{
                      background:
                        "linear-gradient(90deg, rgb(176, 52, 52) 0%, rgb(24, 75, 104) 118.29%)",
                      color: "rgb(255 255 255)",
                      fontFamily: "Inter",
                      fontSize: "24px",
                      padding: "24px 48px",
                    }}
                  >
                    No events
                  </Pill>
                </CustomPill>
              )}
            </StyledCalendar>
          )}
        </Col>
      </CustomRow>
    </>
  );
}
export default ActivityCalendarPage;
